import React, { useState, useEffect, useCallback, useMemo,useContext } from "react";
import "./JobsTableList.css";
import styled from "@emotion/styled";
import workIcon from "../../../Assets/Images/job-icon.svg";
import descriptionIcon from "../../../Assets/Images/description-icon.svg";
import skillsIcon from "../../../Assets/Images/skills-icon.svg";
import tagsIcon from "../../../Assets/Images/tags-icon.svg";
import roleIcon from "../../../Assets/Images/job-role-icon.svg";
import TextInput from "../../../Common/TextInput/TextInput";
import TextArea from "../../../Common/TextArea/TextArea";
import AutoComplete from "../../../Common/AutoComplete/AutoComplete";
import OptionsData from "../../../Assets/Json/StaticOptions.json";
import * as Yup from "yup";
import { useFormik } from "formik";
import CustomTooltip from "../../../Common/Tooltip/Tooltip";
import infoIcon from "../../../Assets/Images/infoicon.svg";
import copyIcon from "../../../Assets/Images/copy-white-icon.png";
import Button from "../../../Common/Button/Button";
import { Toast } from "../../../Common/Toast-Snackbar/ToastSnackbar";
import { DynamicContext } from "../../../Context/DynamicContext";

const JobDetailsContainer = styled.div`
  padding: 14px 14px 0px 14px;
  border-radius: 12px;
`;

const SectionContainer = styled.div`
  margin-bottom: 25px;
  padding-bottom: 15px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  &:last-child {
    border-bottom: none;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  gap: 12px;
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #6860ee;
`;

const SectionIcon = styled.img`
  width: 22px;
  height: 22px;
`;

const JobTitle = styled.h2`
  margin: 0 0 10px 0;
  font-size: 20px;
  font-weight: 600;
  color: var(--header__color);
`;

const DescriptionText = styled.p`
  margin: 0;
  line-height: 1.6;
  color: var(--job-Description_Color);
  font-size: 14px;
  white-space: pre-wrap;
  text-align: left;
`;

const SkillsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 10px;
`;

const Skill = styled.div`
  background-color: #e0deff;
  color: #2d23db;
  padding: 8px 15px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
  display: flex;
  align-items: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  &::before {
    content: "";
    display: inline-block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: #2d23db;
    margin-right: 8px;
  }
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 10px;
`;

const Tag = styled.div`
  background-color: #efe9ff;
  color: #6f42c1;
  padding: 8px 15px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
`;

const RecruitersContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 10px;
`;

const RecruiterChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background-color: #e8f3ff;
  color: #0b5cad;
  padding: 6px 14px 6px 6px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

const RecruiterAvatar = styled.span`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: #0b5cad;
  color: #ffffff;
  font-size: 11px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-transform: uppercase;
`;

const EditableSkillsContainer = styled.div`
  margin-top: 10px;
`;

const JDCopyContanier = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 10px;
`;

function EditSection({ title, children, defaultOpen = true, isEditing }) {
  const [open, setOpen] = React.useState(defaultOpen);

  if (!isEditing) {
    return <div style={{ marginBottom: "16px" }}>{children}</div>;
  }

  return (
    <div
      style={{
        border: "1px solid var(--accordian-border)",
        borderRadius: "12px",
        overflow: "hidden",
        marginBottom: "16px",
      }}
    >
      <div
        onClick={() => setOpen((p) => !p)}
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "16px 20px",
          cursor: "pointer",
          borderBottom: open ? "1px solid var(--accordian-border)" : "none",
          background: "var(--accordian-background)",
        }}
      >
        <span
          style={{
            fontWeight: 700,
            fontSize: "15px",
            color: "var(--header__color)",
          }}
        >
          {title}
        </span>
        <span
          style={{
            color: "var(--text-color-accent, #64748B)",
            fontSize: "13px",
          }}
        >
          {open ? "∧" : "∨"}
        </span>
      </div>

      <div
        style={{
          maxHeight: open ? "2000px" : "0",
          overflow: "hidden",
          transition:
            "max-height 0.3s cubic-bezier(0.4,0,0.2,1), opacity 0.2s ease",
          opacity: open ? 1 : 0,
          padding: open ? "20px 24px" : "0 24px",
          background: "var(--body__background)",
        }}
      >
        {children}
      </div>
    </div>
  );
}

function JobDetails({ job, isEditingJob, onJobChange }) {
  const { StaticOptions = {} } = OptionsData || {};
  const { skills = [] } = StaticOptions || {};
  const {  isHiringTeamTenant } = useContext(DynamicContext);
  const [skillsList, setSkillsInput] = useState([]);
  const [tagsInput, setTagsInput] = useState([]);
  const [autoCompleteKey, setAutoCompleteKey] = useState(0);
  const jobMemo = useMemo(() => job || {}, [job]);
  const assignedRecruiters = Array.isArray(jobMemo.assigned_to)
    ? jobMemo.assigned_to.filter((u) => u?.id || u?.name)
    : [];

  const formatJobDescription = (description) => {
    if (!description) return "No description provided";
    const sections = description.split(/\n\n+/);
    return sections
      .map((section) => {
        if (section.trim().startsWith("-") || section.trim().startsWith("•")) {
          const items = section.split("\n").map((item) => item.trim());
          return items.join("\n");
        }
        return section.trim();
      })
      .join("\n\n");
  };
  const formik = useFormik({
    initialValues: {
      skills: [],
      tags: [],
      title: "",
      job_role: "",
      description: "",
    },
    validationSchema: Yup.object({
      title: Yup.string().required("Job Title is required"),
      job_role: Yup.string().required("Job Role is required"),
      description: Yup.string().required("Job Description is required"),
      skills: Yup.array()
        .min(1, "At least one skill is required")
        .required("Skills are required"),
      tags: Yup.array(),
    }),
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
  });

  const handleCopyJD = async (jobDesc) => {
    if (!jobDesc) return;
    try {
      await navigator.clipboard.writeText(jobDesc);
      Toast("success", "Copied to clipboard");
    } catch (error) {
      console.error("Error copying JD:", error);
      Toast("error", "Failed to copy");
    }
  };
  useEffect(() => {
    if (jobMemo) {
      const initialSkills = jobMemo.skills
        ? Array.isArray(jobMemo.skills)
          ? jobMemo.skills.map((skill) => ({ title: skill }))
          : jobMemo.skills.split(",").map((skill) => ({ title: skill.trim() }))
        : [];

      let initialTags = [];
      const rawTags = jobMemo.tags;
      if (Array.isArray(rawTags)) {
        if (rawTags.length === 1 && typeof rawTags[0] === "string") {
          initialTags = rawTags[0].split(",").map((t) => t.trim());
        } else {
          initialTags = rawTags
            .flatMap((t) =>
              typeof t === "string" ? t.split(",").map((s) => s.trim()) : [],
            )
            .filter((t) => t !== "");
        }
      } else if (typeof rawTags === "string") {
        initialTags = rawTags.split(",").map((t) => t.trim());
      } else {
        initialTags = [];
      }
      initialTags = initialTags.filter(
        (tag, index, self) =>
          tag !== "" &&
          index ===
            self.findIndex((t) => t.toLowerCase() === tag.toLowerCase()),
      );

      if (
        JSON.stringify(formik.values.skills) !==
          JSON.stringify(initialSkills) ||
        JSON.stringify(formik.values.tags) !== JSON.stringify(initialTags) ||
        formik.values.title !== (jobMemo.title || "") ||
        formik.values.job_role !== (jobMemo.job_role || "") ||
        formik.values.description !== (jobMemo.description || "")
      ) {
        formik.setValues({
          skills: initialSkills,
          tags: initialTags,
          title: jobMemo.title || "",
          job_role: jobMemo.job_role || "",
          description: jobMemo.description || "",
        });
      }

      if (JSON.stringify(skillsList) !== JSON.stringify(initialSkills)) {
        setSkillsInput(initialSkills);
      }
      if (JSON.stringify(tagsInput) !== JSON.stringify(initialTags)) {
        setTagsInput(initialTags);
      }
    }
  }, [jobMemo, skillsList, tagsInput, formik]);

  const validateForm = useCallback(async () => {
    const errors = await formik.validateForm();
    formik.setTouched({
      title: true,
      job_role: true,
      description: true,
      skills: true,
      tags: true,
      clientID: true,
    });
    return {
      isValid: Object.keys(errors).length === 0,
      values: formik.values,
    };
  }, [formik]);

  useEffect(() => {
    if (onJobChange) {
      onJobChange({ validateForm });
    }
  }, []);

  const handleFieldChange = useCallback(
    (field, value) => {
      formik.setFieldValue(field, value);
      formik.setFieldTouched(field, true);
      if (onJobChange) {
        onJobChange({ ...jobMemo, [field]: value });
      }
    },
    [formik, onJobChange, jobMemo],
  );

  if (!jobMemo) {
    return <div>Loading job details...</div>;
  }
  return (
    <JobDetailsContainer>
      <EditSection title="Job Details" isEditing={isEditingJob}>
        <SectionContainer>
          <SectionHeader>
            <SectionIcon src={workIcon} alt="Job Title" />
            <SectionTitle>Job Title</SectionTitle>
          </SectionHeader>
          {isEditingJob ? (
            <TextInput
              name="title"
              value={formik.values.title}
              required={true}
              placeholder={"Job Title"}
              onBlur={formik.handleBlur}
              error={formik.touched.title && formik.errors.title}
              errorMessage={formik.errors.title}
              onChange={(e) => handleFieldChange("title", e.target.value)}
            />
          ) : (
            <JobTitle>{job.title}</JobTitle>
          )}
        </SectionContainer>

        {(isEditingJob || jobMemo.client_id || jobMemo.client?.name) && (
          <SectionContainer>
            <SectionHeader>
              <SectionIcon src={workIcon} alt="Client" />
              <SectionTitle>{isHiringTeamTenant ? "Hiring Team" : "Client"}</SectionTitle>
            </SectionHeader>
            <DescriptionText>
              {jobMemo.client
                ? jobMemo.client.contact_person
                  ? `${jobMemo.client.name} - ${jobMemo?.client_contact?.name || jobMemo.client.contact_person}`
                  : jobMemo.client.name
                : "N/A"}
            </DescriptionText>
          </SectionContainer>
        )}
        
        {!isEditingJob && (
          <SectionContainer>
            <SectionHeader>
              <SectionIcon src={roleIcon} alt="Assigned Recruiters" />
              <SectionTitle>Assigned Recruiters</SectionTitle>
            </SectionHeader>
            {assignedRecruiters.length > 0 ? (
              <RecruitersContainer>
                {assignedRecruiters.map((user) => (
                  <RecruiterChip key={user.id || user.name}>
                    <RecruiterAvatar>
                      {(user.name || "?").trim().charAt(0)}
                    </RecruiterAvatar>
                    {user.name || "N/A"}
                  </RecruiterChip>
                ))}
              </RecruitersContainer>
            ) : (
              <DescriptionText>No recruiters assigned</DescriptionText>
            )}
          </SectionContainer>
        )}

        {(job?.job_role || isEditingJob) && (
          <SectionContainer>
            <SectionHeader>
              <SectionIcon src={roleIcon} alt="Job Role" />
              <SectionTitle>Job Role</SectionTitle>
            </SectionHeader>
            {isEditingJob ? (
              <TextInput
                name="job_role"
                value={formik.values.job_role || ""}
                required={true}
                placeholder={"Job role"}
                onBlur={formik.handleBlur}
                error={formik.touched.job_role && formik.errors.job_role}
                errorMessage={formik.errors.job_role}
                onChange={(e) => handleFieldChange("job_role", e.target.value)}
              />
            ) : (
              <DescriptionText>{job.job_role || "N/A"}</DescriptionText>
            )}
          </SectionContainer>
        )}

        <SectionContainer>
          <JDCopyContanier className="jd-copy-container">
            <SectionHeader>
              <SectionIcon src={descriptionIcon} alt="Description" />
              <SectionTitle>Job Description</SectionTitle>
            </SectionHeader>
            {!isEditingJob && (
              <CustomTooltip title={"Copy the job description"}>
              <Button
                label="Copy"
                event={() =>
                  handleCopyJD(formatJobDescription(job.description))
                }
                iconSrc={copyIcon}
                iconWidth={"15px"}
              />
              </CustomTooltip>
            )}
          </JDCopyContanier>
          {isEditingJob ? (
            <TextArea
              name="description"
              value={formik.values.description || ""}
              placeholder={
                "Describe the role in your own words — we’ll turn it into a complete job description."
              }
              onBlur={formik.handleBlur}
              error={formik.touched.description && formik.errors.description}
              errorMessage={formik.errors.description}
              onChange={(e) => handleFieldChange("description", e.target.value)}
              style={{
                height: "160px",
                marginBottom: "10px",
              }}
            />
          ) : (
            <DescriptionText>
              {formatJobDescription(
                job.description || "No description provided",
              )}
            </DescriptionText>
          )}
        </SectionContainer>

        <SectionContainer>
          <SectionHeader>
            <SectionIcon src={skillsIcon} alt="Skills" />
            <div
              className="skill-title"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "5px",
                position: "relative",
              }}
            >
              <SectionTitle>Skills</SectionTitle>
              {isEditingJob && (
                <CustomTooltip
                  title="Type skill(s) and hit Enter or choose from the dropdown"
                  arrow
                  placement="top"
                >
                  <div style={{ display: "inline-block", cursor: "pointer" }}>
                    <img
                      src={infoIcon}
                      alt="Info"
                      className="parser-table-action-icons"
                      style={{
                        margin: "4px 5px 0px 5px",
                        width: "16px",
                        height: "16px",
                      }}
                    />
                  </div>
                </CustomTooltip>
              )}
            </div>
          </SectionHeader>

          {isEditingJob ? (
            <EditableSkillsContainer>
              <AutoComplete
                key={`auto-complete-${autoCompleteKey}`}
                multiple
                placeholder="e.g. JavaScript, React, Node.js, Machine Learning"
                options={skills.filter((skill) => {
                  const selectedSkills = Array.isArray(formik.values.skills)
                    ? formik.values.skills
                    : [];
                  return !selectedSkills.some(
                    (selectedSkill) =>
                      selectedSkill.title?.toLowerCase() ===
                      skill.title.toLowerCase(),
                  );
                })}
                tags={true}
                name="skills"
                style={{ marginTop: "12px" }}
                value={formik.values.skills}
                defaultValues={formik.values.skills}
                onChange={(selectedOptions) => {
                  const validSkills = selectedOptions
                    .map((option) =>
                      typeof option === "string" ? option : option.title,
                    )
                    .filter((option) => option.trim() !== "");

                  const uniqueSkills = validSkills.filter(
                    (skill, index, self) =>
                      index ===
                      self.findIndex(
                        (s) => s.toLowerCase() === skill.toLowerCase(),
                      ),
                  );

                  const formattedSkills = uniqueSkills.map((skill) => ({
                    title: skill,
                  }));
                  formik.setFieldValue("skills", formattedSkills);
                  formik.setFieldTouched("skills", true);
                  handleFieldChange("skills", uniqueSkills);
                }}
                onBlur={() => formik.setFieldTouched("skills", true)}
                InputWrapperStyle={{
                  height: "100px",
                  overflowY: "auto",
                  padding: "10px",
                  marginBottom: "30px",
                }}
                showClearAll={true}
                error={formik.touched.skills && formik.errors.skills}
                errorMessage={formik.errors.skills}
              />
            </EditableSkillsContainer>
          ) : skillsList.length > 0 ? (
            <SkillsContainer>
              {skillsList.map((skill, index) => (
                <Skill key={index}>
                  {typeof skill === "object" ? skill.title : skill}
                </Skill>
              ))}
            </SkillsContainer>
          ) : (
            <DescriptionText>No skills specified</DescriptionText>
          )}
        </SectionContainer>

        {(jobMemo.tags && jobMemo.tags.length > 0) ||
        (isEditingJob && formik.values.tags?.length > 0) ? (
          <SectionContainer>
            <SectionHeader>
              <SectionIcon src={tagsIcon} alt="Tags" />
              <div
                className="skill-title"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                  position: "relative",
                }}
              >
                <SectionTitle>Tags</SectionTitle>
                {isEditingJob && (
                  <CustomTooltip
                    title="Custom tags for better search"
                    arrow
                    placement="top"
                  >
                    <div style={{ display: "inline-block", cursor: "pointer" }}>
                      <img
                        src={infoIcon}
                        alt="Info"
                        className="parser-table-action-icons"
                        style={{
                          margin: "4px 5px 0px 5px",
                          width: "16px",
                          height: "16px",
                        }}
                      />
                    </div>
                  </CustomTooltip>
                )}
              </div>
            </SectionHeader>
            {isEditingJob ? (
              <EditableSkillsContainer>
                <AutoComplete
                  key={`auto-complete-tags-${autoCompleteKey}`}
                  multiple
                  placeholder="Type custom tags and hit Enter (e.g. 'remote', 'full-time', 'senior')"
                  options={[]}
                  tags={true}
                  name="tags"
                  value={formik.values.tags}
                  defaultValues={formik.values.tags}
                  onChange={(selectedOptions) => {
                    const validTags = selectedOptions
                      .map((option) =>
                        typeof option === "string" ? option : option.title,
                      )
                      .filter((option) => option.trim() !== "");

                    const uniqueTags = validTags.filter(
                      (tag, index, self) =>
                        index ===
                        self.findIndex(
                          (t) => t.toLowerCase() === tag.toLowerCase(),
                        ),
                    );

                    formik.setFieldValue("tags", uniqueTags);
                    formik.setFieldTouched("tags", true);
                    handleFieldChange("tags", uniqueTags);
                  }}
                  onBlur={(event) => {
                    formik.setFieldTouched("tags", true);
                    const { value } = event.target;
                    if (value) {
                      const newTags = value
                        .split(",")
                        .map((tag) => tag.trim())
                        .filter(Boolean);
                      const currentTags = formik.values.tags || [];
                      const combined = [...currentTags, ...newTags];
                      const uniqueTags = combined.filter(
                        (tag, index, self) =>
                          index ===
                          self.findIndex(
                            (t) => t.toLowerCase() === tag.toLowerCase(),
                          ),
                      );
                      handleFieldChange("tags", uniqueTags);
                    }
                  }}
                  error={formik.touched.tags && formik.errors.tags}
                  errorMessage={formik.errors.tags}
                  InputWrapperStyle={{
                    height: "100px",
                    color: "var(--header__color) !important",
                    overflowY: "auto",
                    padding: "10px",
                    marginBottom: "10px",
                  }}
                  showClearAll={true}
                />
              </EditableSkillsContainer>
            ) : (
              <TagsContainer>
                {job.tags && job.tags.length > 0 ? (
                  job.tags.map((tag, index) => <Tag key={index}>{tag}</Tag>)
                ) : (
                  <DescriptionText>No tags specified</DescriptionText>
                )}
              </TagsContainer>
            )}
          </SectionContainer>
        ) : null}
      </EditSection>
    </JobDetailsContainer>
  );
}

export default JobDetails;